// Module 11 — the WORKSPACE: a dockable, collapsible, persisted panel system so the user can keep open
// only what they need and close the rest, maximising the map. Every panel has a header with collapse +
// close, and is toggled from a launcher chip-bar. Layout (open/collapsed/position per panel) persists to
// a UiStorage seam. All visual state is driven by CSS CLASSNAMES + per-panel CSS CUSTOM PROPERTIES (never
// direct geometry inline style), so the whole thing runs headless under the test fakes. NON-CONTRACT seam
// (lives behind the frozen mountDashboards).
//
// P-DESKTOP-FLOAT (2026-07-06 pt.5): most panels are now independently DRAGGABLE FLOATING WINDOWS on
// desktop (`def.float`), replacing the earlier two-edge-drawer layout entirely — user report + annotated
// screenshot: the drawer's stacked, scrolling panel columns looked "cut/sliced" (charts running past the
// visible scroll region with no obvious affordance), and asked for real OS-window-style floating,
// draggable panels for everything except the launcher, Controls, and the Consumption/Generation charts.
// Those few stay in a small non-scrolling `.fixed-rail` (mount.ts/index.html), each floating panel is
// `position:fixed` at a user-dragged (or cascaded-default) `{x,y}`, brought to front on interaction via a
// monotonic z-index counter. The actual pointer-drag GESTURE needs live mouse/pointer coordinates the
// narrow `UiElement.addEventListener(type, handler: () => void)` seam can't carry (handlers take no event
// arg) — so, like element-clearance.ts's ResizeObserver, the real drag mechanics live in
// `app/draggable-panels.ts` (real DOM), calling back into `setPanelPosition`/`bringToFront` below. This
// module only tracks/persists/paints the resulting position — genuinely headless-testable, no browser APIs.
import type { UiState } from "../state/index";
import type { UiDocument, UiElement, UiStorage } from "./seam";
import { tip, PANEL_CHROME_TIPS } from "./help-registry";

export type DockCorner = "tl" | "tr" | "bl" | "br";

// One registered panel. `build` populates the body host once (eager) and returns optional live hooks.
export interface PanelDef {
  id: string;
  title: string;
  dock: DockCorner; // vestigial on desktop since P-DESKTOP-FLOAT (kept for mobile's per-panel CSS, which
  //                   still reads dock-${corner} for closed-tab styling); no longer determines floating
  //                   position at all — see `float` below.
  defaultOpen: boolean;
  persist?: boolean; // default true; set false for selection-driven panels (inspector) so a transient
  //                    open state is NOT remembered open-but-empty across reloads
  group?: string; // launcher grouping (P14b): chips cluster under a small group label so the bar stops
  //                 cramming ~18 flat toggles into one unreadable row. Absent ⇒ the default "Panels" group.
  pinned?: boolean; // P14b: permanent chrome — always open, no close button, no launcher chip (e.g. the
  //                   system-totals headline). Pinned panels ignore saved/default open state and persistence.
  float?: boolean; // P-DESKTOP-FLOAT: renders as an independently draggable floating window (position:fixed
  //                  at a user-dragged/cascaded {x,y}, own z-index) instead of the small fixed panel rail.
  //                  Never combined with `pinned` in practice; the CSS structurally excludes pinned panels
  //                  from the floating rule regardless (the same specificity-bug lesson mobile already hit).
  fixedRight?: boolean; // renders as a fixed, non-floating, non-draggable panel pinned to the right edge of
  //                       the screen (mirrors the left `.fixed-rail`) — used for a panel the user wants to
  //                       keep persistently visible in a stable spot (e.g. Inspector, so the panel showing a
  //                       selected object's details doesn't get lost among the draggable floating windows
  //                       when clicking around the map to inspect different objects). Never combined with
  //                       `float` in practice.
  build(host: UiElement): PanelInstance | void;
  onClose?(): void; // e.g. the inspector deselects when its panel is closed
  onOpen?(): void;
  help?: string; // Wave 2: a one-line "what this panel/chart shows" hint, surfaced as a tooltip on the launcher
  //                chip and the panel title (for charts this is the ChartDef.subtitle — previously hidden until open).
}
export interface PanelInstance {
  // called per snapshot for EVERY panel with its current open state. Cheap/data-accumulating work (e.g.
  // a chart's ring buffer) runs regardless; expensive draw work should gate on `open`. This lets charts
  // keep full history while closed and render it instantly on open.
  update?(s: UiState, open: boolean): void;
  destroy?(): void;
}

export interface WorkspaceDeps {
  doc: UiDocument;
  panels: PanelDef[];
  storage?: UiStorage; // absent ⇒ layout is in-memory only (no persistence) — used in tests
  storageKey?: string; // default "gta:workspace.v1"
  // P-MOBILE-FUNDAMENTAL (2026-07-06 pt.4): when true, opening a panel closes EVERY other open,
  // non-pinned panel first — GLOBALLY, not just same-corner. Only ever set true on a detected phone
  // (mount.ts's mobileTabMode); desktop leaves it unset, so multiple floating panels stay open together.
  singleOpenGlobal?: boolean;
}

export interface Workspace {
  mount(root: UiElement): void;
  update(s: UiState): void; // fan out to OPEN panels' instances
  open(id: string): void;
  close(id: string): void;
  toggle(id: string): void;
  setCollapsed(id: string, collapsed: boolean): void;
  isOpen(id: string): boolean;
  // P-DESKTOP-FLOAT: move a floating panel to a new screen position (called by app/draggable-panels.ts's
  // real pointer-drag handler) and bump it to the front of the floating stack. No-ops for a non-floating
  // (or unknown) panel id — a defensive guard, not a documented contract for callers to rely on.
  setPanelPosition(id: string, x: number, y: number): void;
  bringToFront(id: string): void;
  destroy(): void;
}

interface Pos { x: number; y: number }
interface PanelState { open: boolean; collapsed: boolean; pos?: Pos; z?: number }
interface Mounted {
  def: PanelDef;
  instance: PanelInstance | null;
  panelEl: UiElement; // the .wpanel wrapper (className carries open/collapsed/floating)
  chipEl: UiElement; // the launcher chip (className carries active)
  state: PanelState;
  moved: boolean; // Wave 1: true once the user has dragged this float OR it was restored from a saved position.
                  // An UNMOVED float gets a fresh open-order cascade slot on open (fixes the stack-on-open bug).
}

// P-DESKTOP-FLOAT: a floating panel's on-first-open default position, cascaded by FLOAT registration
// order (not overall panel order) so newly-opened floating panels don't spawn stacked exactly on top of
// each other. Wraps after 12 so a long session of "open everything" doesn't cascade off-screen; the user
// can drag from there regardless. No viewport dimensions needed (workspace.ts stays headless) — 420px
// clears the ~360px-wide fixed-rail on the left so a freshly-opened floating panel doesn't spawn directly
// under it.
function cascadePos(floatIndex: number): Pos {
  const i = floatIndex % 12;
  return { x: 420 + i * 30, y: 90 + i * 26 };
}
const BASE_Z = 20;
const Z_CAP = 150; // keep floating-panel z well below the launcher (index.html .wks-launcher z:200); reindex here

export function makeWorkspace(deps: WorkspaceDeps): Workspace {
  const { doc, panels } = deps;
  const key = deps.storageKey ?? "gta:workspace.v1";
  const mountedById = new Map<string, Mounted>();
  const order: string[] = [];
  let lastState: UiState | null = null;
  let nextZ = BASE_Z;
  let openCascadeSeq = 0; // Wave 1: how many unmoved floats have been opened this session — drives the cascade slot

  // Wave 1: bring the caller's panel to the front. nextZ climbs by 1 per open/focus; if it would reach the cap
  // we first COMPACT the open floats back down to BASE_Z.. (by current z-order) so z never grows unbounded and
  // never climbs into the launcher (z:200). Returns the new top z for the caller to assign.
  function reindexZ(): void {
    const floats = order.map((id) => mountedById.get(id)!).filter((m) => m.def.float && m.state.open);
    floats.sort((a, b) => (a.state.z ?? BASE_Z) - (b.state.z ?? BASE_Z));
    let z = BASE_Z;
    for (const m of floats) { m.state.z = z++; paintPanel(m); }
    nextZ = z - 1;
  }
  function bumpZ(): number {
    if (nextZ + 1 > Z_CAP) reindexZ();
    nextZ += 1;
    return nextZ;
  }

  // ── persistence ──────────────────────────────────────────────────────────────────
  function loadSaved(): Record<string, Partial<PanelState>> {
    if (!deps.storage) return {};
    try {
      const raw = deps.storage.getItem(key);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === "object" ? (parsed as Record<string, Partial<PanelState>>) : {};
    } catch {
      return {}; // corrupt entry → fall back to defaults, never throw
    }
  }
  function save(): void {
    if (!deps.storage) return;
    const out: Record<string, PanelState> = {};
    for (const id of order) {
      const m = mountedById.get(id)!;
      if (m.def.persist === false || m.def.pinned) continue; // selection-driven + pinned-chrome panels are never remembered
      out[id] = { ...m.state };
    }
    try { deps.storage.setItem(key, JSON.stringify(out)); } catch { /* private mode / quota — ignore */ }
  }

  // ── className/style helpers (the ONLY way visual state is expressed) ───────────────
  function paintPanel(m: Mounted): void {
    // `pinned` is a real, load-bearing CSS class (not decorative): index.html's floating-panel rules (both
    // desktop's `.wpanel.floating:not(.pinned)` and mobile's `.wpanel.open:not(.pinned)`) key off it so
    // permanent chrome (System totals) can NEVER match them, however many other classes it carries — see
    // those rules' comments for the specificity bug this closes (a pinned panel is always "open", so a
    // 3-class floating-panel rule used to outrank a lower-specificity single-class override meant to keep
    // it in normal flow).
    m.panelEl.className =
      `wpanel wpanel-${m.def.id} dock-${m.def.dock}` +
      (m.def.pinned ? " pinned" : "") +
      (m.def.float ? " floating" : "") +
      (m.state.open ? " open" : "") + (m.state.collapsed ? " collapsed" : "");
    // P-DESKTOP-FLOAT: position/z-index are published as CSS CUSTOM PROPERTIES, never as direct `left`/
    // `top`/`position` inline style — a real (not hypothetical) precedence hazard: an inline `style` beats
    // ANY class selector including mobile's higher-specificity `.wpanel.open:not(.pinned)` rule, which
    // would otherwise get silently overridden by a naive `left:Xpx` inline style on the exact same
    // element. A custom property is inert until a normal CSS rule consumes it (`left: var(--wx)`), so the
    // cascade — mobile's rule naturally winning by specificity when `gta-mobile-tabs` is set, desktop's
    // rule otherwise — works exactly as it would with no inline style involved at all.
    if (m.def.float) {
      const p = m.state.pos ?? { x: 420, y: 90 };
      const z = m.state.z ?? BASE_Z;
      m.panelEl.setAttribute?.("style", `--wx:${Math.round(p.x)}px;--wy:${Math.round(p.y)}px;--wz:${z}`);
    }
  }
  function paintChip(m: Mounted): void {
    m.chipEl.className = "wks-chip" + (m.state.open ? " active" : "");
  }

  function applyOpen(m: Mounted, open: boolean): void {
    if (m.state.open === open) return;
    // Global open-exclusivity — closing every other open, non-pinned panel first means at most one can
    // ever be open at once. NOT currently wired to any caller (P-MOBILE-V2, 2026-07-07 pt.7, retired the
    // one caller that used this for phones — mobile now stacks multiple panels in-flow inside one bottom
    // drawer instead, where simultaneous panels can never overlap by construction, so exclusivity is no
    // longer needed to prevent that). Kept as a general, tested Workspace capability (see this file's own
    // `singleOpenGlobal` tests) in case a future caller wants strict single-panel-open semantics again.
    if (open && deps.singleOpenGlobal) {
      for (const id of order) {
        if (id === m.def.id) continue;
        const sibling = mountedById.get(id)!;
        if (!sibling.def.pinned && sibling.state.open) applyOpen(sibling, false);
      }
    }
    m.state.open = open;
    if (open && m.def.float) {
      // Wave 1: an UNMOVED float gets a fresh OPEN-ORDER cascade slot so opening several panels steps them
      // down-right instead of stacking them all on the registration-default {420,90} (the old floatIndex%12
      // collision — ~30 float panels shared 12 slots, so 0/12/24 spawned identically and only the top was
      // usable). A dragged/restored panel (moved=true) keeps its own position.
      if (!m.moved) { m.state.pos = cascadePos(openCascadeSeq++); m.moved = true; }
      m.state.z = bumpZ(); // bring it to front (bounded — never climbs into the launcher)
    }
    paintPanel(m); paintChip(m);
    if (open) {
      m.def.onOpen?.();
      if (lastState && m.instance?.update) m.instance.update(lastState, true); // refresh immediately on open
    } else {
      m.def.onClose?.();
    }
    save();
  }

  const api: Workspace = {
    mount(root: UiElement): void {
      const launcher = doc.createElement("div");
      launcher.className = "wks-launcher";
      const title = doc.createElement("span");
      title.className = "wks-brand";
      title.textContent = "Panels";
      launcher.append(title);

      // P-DESKTOP-FLOAT: a single small, non-scrolling, non-collapsible container for the handful of
      // panels that stay fixed in place (Controls + the two headline charts), plus the always-open
      // pinned totals headline. Everything else (`def.float`) is appended directly to `root` instead —
      // each one is independently `position:fixed` (painted above), so its DOM parent doesn't matter for
      // layout; keeping them out of any shared scrollable/collapsible ancestor is what makes them
      // immune to the old drawer-collapse "position:fixed hijacked by an ancestor transform" hazard by
      // construction, not by a defensive CSS override.
      const fixedRail = doc.createElement("div");
      fixedRail.className = "fixed-rail";

      // a second small, non-scrolling, non-draggable rail pinned to the RIGHT edge — currently the sole
      // home for the Inspector (`def.fixedRight`), kept out of `fixedRail` (the left rail) so it can get
      // its own dedicated screen real estate instead of sharing space with Controls/charts.
      const rightRail = doc.createElement("div");
      rightRail.className = "right-rail";

      const saved = loadSaved();
      // launcher chips cluster under their group label (first-seen order) so ~18 panels read as a few
      // labelled clusters instead of one cramped row. Pinned panels emit no chip (permanent chrome).
      const groupOrder: string[] = [];
      const chipsByGroup = new Map<string, UiElement[]>();
      for (const def of panels) {
        // panel wrapper: header (title + collapse + close) over a body host
        const panelEl = doc.createElement("div");
        const header = doc.createElement("div");
        header.className = "wpanel-head";
        // a real <button>, not a <span> — it's a genuine tap target (the mobile corner-tab's ONLY way
        // to open a panel; adversarial review, 2026-07-06: a span has no tab-stop / Enter-Space
        // activation / :focus-visible ring for keyboard or switch-control users).
        const ttl = doc.createElement("button");
        ttl.className = "wpanel-title";
        ttl.textContent = def.title;
        ttl.setAttribute?.("type", "button");
        tip(ttl, PANEL_CHROME_TIPS.titleBtn(def.title, def.help)); // Wave 2: title/aria = "title — what it shows"
        const collapseBtn = doc.createElement("button");
        collapseBtn.className = "wpanel-btn wpanel-collapse";
        collapseBtn.textContent = "–";
        tip(collapseBtn, PANEL_CHROME_TIPS.collapse(def.title)); // icon-only → name it for SR/a11y + hover
        header.append(ttl, collapseBtn);
        // pinned panels are permanent chrome: no close button (can't be dismissed)
        if (!def.pinned) {
          const closeBtn = doc.createElement("button");
          closeBtn.className = "wpanel-btn wpanel-close";
          closeBtn.textContent = "✕";
          tip(closeBtn, PANEL_CHROME_TIPS.close(def.title));
          closeBtn.addEventListener("click", () => api.close(def.id));
          header.append(closeBtn);
        }
        const body = doc.createElement("div");
        body.className = "wpanel-body";
        panelEl.append(header, body);

        const chipEl = doc.createElement("button");
        chipEl.textContent = def.title;
        tip(chipEl, PANEL_CHROME_TIPS.chip(def.title, def.help)); // Wave 2: launcher chip explains the panel/chart

        const savedState = def.persist === false ? undefined : saved[def.id]; // non-persisted → ignore storage
        const willOpen = def.pinned ? true : typeof savedState?.open === "boolean" ? savedState.open : def.defaultOpen;
        const hasSavedPos = savedState?.pos !== undefined;
        // Wave 1: ONE cascade counter (openCascadeSeq) for every float. A float with no saved position takes its
        // slot when it FIRST becomes open — either NOW (default-open) or on the first user-open (applyOpen). This
        // unifies what used to be two counters (registration floatIndex vs open) that both started at 0 and could
        // land a user-opened panel exactly on a default-open one. A default-closed, un-saved float gets a harmless
        // fallback until opened. Once a float has taken its slot (default-open) it is `moved` so re-open keeps it.
        const defaultPos: Pos | undefined = def.float
          ? (hasSavedPos ? savedState!.pos! : willOpen ? cascadePos(openCascadeSeq++) : { x: 420, y: 90 })
          : undefined;
        // Wave 1: clamp the restored z to Z_CAP — an old session (pre-cap) could have saved an unbounded z that
        // would otherwise render a restored panel over the launcher until its first focus.
        const restoredZ = Math.min(savedState?.z ?? BASE_Z, Z_CAP);
        if (def.float) nextZ = Math.max(nextZ, restoredZ);
        const state: PanelState = {
          // pinned → forced open (chrome); else restore saved, else the panel's default
          open: willOpen,
          collapsed: typeof savedState?.collapsed === "boolean" ? savedState.collapsed : false,
          ...(def.float ? { pos: defaultPos!, z: restoredZ } : {}),
        };
        const instance = def.build(body) ?? null;
        // moved=true when it has a saved position (user placed it last session) OR it just took its default-open
        // cascade slot — either way re-opening it must NOT reassign a new slot.
        const m: Mounted = { def, instance, panelEl, chipEl, state, moved: hasSavedPos || (def.float === true && willOpen) };
        mountedById.set(def.id, m);
        order.push(def.id);
        if (def.float) root.append(panelEl);
        else if (def.fixedRight) rightRail.append(panelEl);
        else fixedRail.append(panelEl);
        paintPanel(m); paintChip(m);

        collapseBtn.addEventListener("click", () => api.setCollapsed(def.id, !m.state.collapsed));
        // Tap-to-open on the title itself (mobile corner-tab UX, P-MOBILE): on a phone a closed panel's
        // header IS its tab (index.html's `html.gta-mobile-tabs` block keeps the header visible + shrunk
        // when `!open`, instead of the desktop `display:none`), so the title needs to be directly
        // touchable. `open()` no-ops if already open, so this is inert on desktop too (a closed panel's
        // header is `display:none` there — nothing to tap — and tapping an already-open panel's title
        // does nothing, same as today).
        ttl.addEventListener("click", () => api.open(def.id));
        if (!def.pinned) {
          chipEl.addEventListener("click", () => api.toggle(def.id));
          const g = def.group ?? "Panels";
          if (!chipsByGroup.has(g)) { chipsByGroup.set(g, []); groupOrder.push(g); }
          chipsByGroup.get(g)!.push(chipEl);
        }
      }

      // render the grouped chip clusters into the launcher (after the brand label). Wave 4c: each group header
      // is a BUTTON that collapses/expands its chips, so the launcher reads as a short list of topic headers
      // instead of ~30 chips. The FIRST group (Control) starts expanded; the rest start collapsed on a first
      // visit; the user's per-group choice is remembered via the storage seam.
      const COLLAPSE_KEY = "gta:launcher.collapsed.v1";
      const savedCollapse: string[] | null = (() => {
        try { const r = deps.storage?.getItem(COLLAPSE_KEY); return r ? (JSON.parse(r) as string[]) : null; } catch { return null; }
      })();
      const collapsed = new Set<string>(savedCollapse ?? []);
      const firstGroup = groupOrder[0];
      for (const g of groupOrder) {
        if (savedCollapse === null && g !== firstGroup) collapsed.add(g); // first visit: collapse all but Control
        const grp = doc.createElement("div");
        const lbl = doc.createElement("button");
        lbl.className = "wks-group-label";
        lbl.setAttribute?.("type", "button");
        const chips = chipsByGroup.get(g)!;
        const paint = (): void => {
          grp.className = "wks-group" + (collapsed.has(g) ? " collapsed" : "");
          lbl.textContent = `${collapsed.has(g) ? "▸" : "▾"} ${g} (${chips.length})`;
        };
        tip(lbl, `Show or hide the ${chips.length} ${g} panels.`);
        lbl.addEventListener("click", () => {
          if (collapsed.has(g)) collapsed.delete(g); else collapsed.add(g);
          paint();
          try { deps.storage?.setItem(COLLAPSE_KEY, JSON.stringify([...collapsed])); } catch { /* private mode — ignore */ }
        });
        paint();
        grp.append(lbl, ...chips);
        launcher.append(grp);
      }

      // P-MOBILE bugfix (2026-07-06 pt.2, found live; widened pt.4 to global exclusivity): exclusivity
      // was only enforced inside applyOpen() — i.e. on a user-triggered open/toggle AFTER mount — never
      // on the INITIAL state restored from storage above. A user who had several panels open in an
      // earlier (desktop) session, then loaded the app as a detected phone, got EVERY one of those saved-
      // open panels rendered simultaneously. Reconcile once, right after mount: keep only the first-
      // registered open, non-pinned panel GLOBALLY; close the rest (a real close — fires onClose,
      // repaints, persists — not a silent state edit).
      if (deps.singleOpenGlobal) {
        let keptOpen = false;
        for (const id of order) {
          const m = mountedById.get(id)!;
          if (m.def.pinned || !m.state.open) continue;
          if (keptOpen) applyOpen(m, false);
          else keptOpen = true;
        }
      }

      // launcher first (top chrome), then the small fixed-panel rail and the right-side rail (root already
      // holds every floating panel, appended above as each was constructed).
      root.append(launcher, fixedRail, rightRail);
    },

    update(s: UiState): void {
      lastState = s;
      for (const id of order) {
        const m = mountedById.get(id)!;
        m.instance?.update?.(s, m.state.open); // ALL panels: charts accumulate while closed; each gates draw on `open`
      }
    },

    open(id: string): void { const m = mountedById.get(id); if (m) applyOpen(m, true); },
    close(id: string): void { const m = mountedById.get(id); if (m) applyOpen(m, false); },
    toggle(id: string): void { const m = mountedById.get(id); if (m) applyOpen(m, !m.state.open); },
    setCollapsed(id: string, collapsed: boolean): void {
      const m = mountedById.get(id);
      if (!m || m.state.collapsed === collapsed) return;
      m.state.collapsed = collapsed;
      paintPanel(m);
      save();
    },
    isOpen(id: string): boolean { return mountedById.get(id)?.state.open ?? false; },

    setPanelPosition(id: string, x: number, y: number): void {
      const m = mountedById.get(id);
      if (!m || !m.def.float) return; // defensive no-op — not a documented contract for callers to rely on
      m.state.pos = { x, y };
      m.moved = true; // Wave 1: a user-dragged panel keeps its position — no cascade reassignment on re-open
      paintPanel(m);
      save();
    },
    bringToFront(id: string): void {
      const m = mountedById.get(id);
      if (!m || !m.def.float) return;
      m.state.z = bumpZ();
      paintPanel(m);
      save();
    },

    destroy(): void {
      for (const id of order) mountedById.get(id)!.instance?.destroy?.();
      mountedById.clear();
      order.length = 0;
      lastState = null;
    },
  };
  return api;
}
